import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Main API client for admin routes (with credentials)
export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public API client for customer-facing routes (NO credentials - fixes Safari mobile issues)
export const publicApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: false,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========================================
// AUTH TOKEN MANAGEMENT
// ========================================
const TOKEN_KEY = 'rts_admin_token';

export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setStoredToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeStoredToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses (auto logout and redirect)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAdminRoute = error.config?.url?.includes('/admin') || error.config?.url?.includes('/auth/admin');
      if (isAdminRoute) {
        removeStoredToken();
        // Only redirect if we're on an admin page (not login page)
        if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ========================================
// PUBLIC QUOTE API (NO AUTH REQUIRED)
// Uses publicApi to avoid Safari mobile cookie issues
// ========================================
export const createQuote = async (data) => {
  const response = await publicApi.post('/quotes', data);
  return response.data;
};

export const getQuote = async (quoteId) => {
  const response = await publicApi.get(`/quotes/${quoteId}`);
  return response.data;
};

// Payment API (PUBLIC)
export const createCheckoutSession = async (quoteId) => {
  const response = await publicApi.post(`/quotes/${quoteId}/checkout`);
  return response.data;
};

export const getCheckoutStatus = async (sessionId) => {
  const response = await publicApi.get(`/checkout/status/${sessionId}`);
  return response.data;
};

// WhatsApp API (PUBLIC after payment)
export const getWhatsAppLink = async (quoteId) => {
  const response = await publicApi.get(`/whatsapp-link/${quoteId}`);
  return response.data;
};

// ========================================
// ADMIN API (AUTH REQUIRED)
// ========================================
export const adminLogin = async (credentials) => {
  // Login does NOT need withCredentials - it returns JWT token in response body
  // Using publicApi to avoid CORS issues with credentials
  const response = await publicApi.post('/auth/admin/login', credentials);
  if (response.data?.access_token) {
    setStoredToken(response.data.access_token);
  }
  return response.data;
};

export const adminLogout = async () => {
  try {
    const token = getStoredToken();
    if (token) {
      await publicApi.post('/auth/admin/logout', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }
  } finally {
    removeStoredToken();
  }
};

export const verifyAdminToken = async () => {
  try {
    const token = getStoredToken();
    if (!token) return null;
    
    // Use publicApi with manual auth header to avoid CORS issues with withCredentials
    const response = await publicApi.get('/auth/admin/verify', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    removeStoredToken();
    return null;
  }
};

export const isAuthenticated = () => {
  return !!getStoredToken();
};

export const changeAdminPassword = async (currentPassword, newPassword, confirmPassword) => {
  const response = await api.post('/auth/admin/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
  return response.data;
};

// 2FA verification during login
// Does NOT need withCredentials - it returns JWT token in response body
export const verify2FALogin = async ({ code, temp_token, use_backup = false }) => {
  const params = new URLSearchParams();
  params.append('code', code);
  params.append('temp_token', temp_token);
  params.append('use_backup', use_backup.toString());
  
  const response = await publicApi.post(`/auth/admin/verify-2fa?${params.toString()}`);
  
  // Store token if successful
  if (response.data?.access_token) {
    setStoredToken(response.data.access_token);
  }
  
  return response.data;
};

export const getAllQuotes = async (status = null, archived = null) => {
  const params = {};
  if (status) params.status = status;
  if (archived !== null) params.archived = archived;
  const response = await api.get('/admin/quotes', { params });
  return response.data;
};

export const bulkArchiveQuotes = async (ids, reason = null) => {
  const response = await api.post('/admin/requests/archive-bulk', { ids, reason });
  return response.data;
};

export const bulkUnarchiveQuotes = async (ids) => {
  const response = await api.post('/admin/requests/unarchive-bulk', { ids });
  return response.data;
};

// Delete quote (permanent deletion)
export const deleteQuoteRequest = async (quoteId) => {
  const response = await api.delete(`/admin/requests/${quoteId}`);
  return response.data;
};

// Delete unpaid ACI
export const deleteAdditionalInvoice = async (invoiceId) => {
  const response = await api.delete(`/admin/additional-invoices/${invoiceId}`);
  return response.data;
};

// Sync ACI payment status with Stripe
export const syncACIPaymentStatus = async (invoiceId) => {
  const response = await api.post(`/admin/additional-invoices/${invoiceId}/sync-payment`);
  return response.data;
};

export const archiveQuote = async (quoteId, reason = null) => {
  const params = reason ? { reason } : {};
  const response = await api.post(`/admin/quotes/${quoteId}/archive`, null, { params });
  return response.data;
};

export const unarchiveQuote = async (quoteId) => {
  const response = await api.post(`/admin/quotes/${quoteId}/unarchive`);
  return response.data;
};

export const generateQuote = async (quoteId, price) => {
  const response = await api.put(`/admin/quotes/${quoteId}/generate-quote`, { price });
  return response.data;
};

export const updateQuoteStatus = async (quoteId, status) => {
  const response = await api.put(`/admin/quotes/${quoteId}/status?status=${status}`);
  return response.data;
};

export const uploadPOD = async (quoteId, file) => {
  const formData = new FormData();
  formData.append('pod_image', file);
  const response = await api.post(`/admin/quotes/${quoteId}/pod`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

// ========================================
// ADMIN INVOICES API
// ========================================
export const getAdminInvoices = async (status = 'paid') => {
  const response = await api.get('/admin/invoices', { params: { status } });
  return response.data;
};

export const getInvoicePdfUrl = (invoiceId) => {
  return `${BACKEND_URL}/api/admin/invoices/${invoiceId}/pdf`;
};

// Download invoice PDF with authentication (returns Blob)
export const downloadInvoicePdf = async (invoiceId) => {
  const token = getStoredToken();
  const response = await fetch(`${BACKEND_URL}/api/admin/invoices/${invoiceId}/pdf`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) throw new Error('Failed to download invoice PDF');
  return response.blob();
};

// Open invoice PDF in new tab with authentication
export const openInvoicePdf = async (invoiceId) => {
  const blob = await downloadInvoicePdf(invoiceId);
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Clean up after a delay to allow the browser to load the PDF
  setTimeout(() => window.URL.revokeObjectURL(url), 10000);
};

// Print invoice PDF with authentication
export const printInvoicePdf = async (invoiceId) => {
  const blob = await downloadInvoicePdf(invoiceId);
  const url = window.URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
    };
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 30000);
};

export const getDriverJobSheetPdf = async (quoteId) => {
  const response = await api.get(`/admin/requests/${quoteId}/driver-job-sheet-pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

export const sendInvoiceEmail = async (invoiceId) => {
  const response = await api.post(`/admin/invoices/${invoiceId}/send`);
  return response.data;
};

export const getInvoiceShareLink = async (invoiceId) => {
  const response = await api.get(`/admin/invoices/${invoiceId}/share-link`);
  return response.data;
};

// ========================================
// BULK INVOICE MANAGEMENT (OWNER ONLY)
// ========================================
export const generateBulkInvoiceSummary = async (invoiceIds) => {
  const response = await api.post('/admin/invoices/bulk-summary', { invoice_ids: invoiceIds });
  return response.data;
};

export const getBulkInvoiceSummaryPdfUrl = () => {
  return `${BACKEND_URL}/api/admin/invoices/bulk-summary-pdf`;
};

export const generateBulkInvoiceSummaryPdf = async (invoiceIds) => {
  const token = getStoredToken();
  const response = await fetch(`${BACKEND_URL}/api/admin/invoices/bulk-summary-pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ invoice_ids: invoiceIds })
  });
  if (!response.ok) throw new Error('Failed to generate summary PDF');
  return response.blob();
};

// Distance API (PUBLIC)
export const calculateDistance = async (origin, destination) => {
  const response = await publicApi.post('/distance', {
    origin_postcode: origin,
    destination_postcode: destination,
  });
  return response.data;
};

// ========================================
// SITE ANALYTICS (OWNER ONLY)
// ========================================

// Track site visit (PUBLIC - called from customer site)
export const trackSiteVisit = async () => {
  try {
    await publicApi.post('/track-visit');
  } catch (err) {
    // Silently fail - don't impact user experience
  }
};

// Get owner stats (OWNER ONLY)
export const getOwnerStats = async () => {
  const response = await api.get('/admin/owner-stats');
  return response.data;
};

export default api;
